<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable prettier/prettier, vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, inject, proxyRefs, ref } from 'vue';
import ImudeiRuntimeActionPanel from './ImudeiRuntimeActionPanel.vue';
import { recordIdFromResponse } from './imudeiResponse';
import { useImudeiRuntimeModel } from './useImudeiRuntimeModel';

const props = defineProps({
  module: { type: String, required: true },
  screen: { type: String, required: true },
});

const emit = defineEmits(['navigate']);
const realData = inject('imudeiRealData');
const { error, saving } = realData;
const model = proxyRefs(
  useImudeiRuntimeModel({
    realData,
    module: computed(() => props.module),
    screen: computed(() => props.screen),
  })
);

const presentationByModule = Object.freeze({
  brokers: {
    action: 'Salvar perfil',
    icon: 'i-lucide-contact-round',
    panels: ['Dados profissionais', 'Capacidades', 'Visão pública'],
    stages: ['Perfil', 'Documentos', 'Disponibilidade', 'Ativo'],
    tabs: ['Visão geral', 'Perfil público', 'Capacidades', 'Histórico'],
  },
  commissions: {
    action: 'Salvar comissão',
    icon: 'i-lucide-badge-percent',
    panels: ['Rateio da comissão', 'Parcelas e valores', 'Auditoria'],
    stages: ['Lançamento', 'Participantes', 'Conferência', 'Pagamento'],
    tabs: ['Resumo', 'Rateio', 'Participantes', 'Documentos', 'Histórico'],
  },
  finance: {
    action: 'Salvar lançamento',
    icon: 'i-lucide-landmark',
    panels: ['Detalhes do lançamento', 'Conferência', 'Conciliação'],
    stages: ['Lançamento', 'Conferência', 'Conciliação', 'Concluído'],
    tabs: ['Resumo', 'Parcelas', 'Rateio', 'Documentos', 'Histórico'],
  },
  postSale: {
    action: 'Salvar processo',
    icon: 'i-lucide-key-round',
    panels: ['Prazos e responsáveis', 'Documentos do processo', 'Acompanhamento'],
    stages: ['Cadastro', 'Documentos', 'Acompanhamento', 'Conclusão'],
    tabs: ['Visão geral', 'Participantes', 'Documentos', 'Histórico'],
  },
  proposals: {
    action: 'Salvar proposta',
    icon: 'i-lucide-handshake',
    panels: ['Valores e condições', 'Participantes', 'Negociação'],
    stages: ['Negociação', 'Documentação', 'Contrato', 'Finalização'],
    tabs: ['Visão geral', 'Participantes', 'Documentos', 'Histórico'],
  },
  sales: {
    action: 'Salvar venda',
    icon: 'i-lucide-house',
    panels: ['Dados da venda', 'Participantes', 'Financeiro'],
    stages: ['Cadastro', 'Documentos', 'Assinatura', 'Pós-venda'],
    tabs: ['Visão geral', 'Participantes', 'Documentos', 'Financeiro', 'Histórico'],
  },
  visits: {
    action: 'Salvar visita',
    icon: 'i-lucide-calendar-check-2',
    panels: ['Dados do agendamento', 'Participantes', 'Checklist da visita'],
    stages: ['Agendamento', 'Confirmação', 'Visita', 'Resultado'],
    tabs: ['Visão geral', 'Participantes', 'Checklist', 'Arquivos', 'Histórico'],
  },
});

const asArray = value => (Array.isArray(value) ? value : []);
const firstText = (...values) =>
  values.find(value => typeof value === 'string' && value.trim()) || '';
const initialsFor = value =>
  String(value || '—')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toLocaleUpperCase('pt-BR');
const statusTone = value => {
  const status = String(value || '').toLocaleLowerCase('pt-BR');
  if (/final|conclu|aceit|pago|ativo|encerr/.test(status)) {
    return 'bg-emerald-50 text-emerald-700';
  }
  if (/cancel|recus|atras|erro/.test(status)) return 'bg-rose-50 text-rose-700';
  if (/docum|confirm|confer|agenda/.test(status)) return 'bg-sky-50 text-sky-700';
  return 'bg-violet-50 text-[#5a24c7]';
};

const presentation = computed(
  () => presentationByModule[props.module] || presentationByModule.sales
);
const isCreate = computed(() => props.screen === 'create');
const isEdit = computed(() => props.screen === 'edit');
const isDetail = computed(() => props.screen === 'detail');
const isSalesEdit = computed(
  () => props.module === 'sales' && props.screen === 'edit'
);
const isSalesCreate = computed(
  () => props.module === 'sales' && props.screen === 'create'
);
const hasDetail = computed(() => Boolean(model.detail?.id));
const source = computed(() => model.detail?.source || {});
const title = computed(() => {
  if (isCreate.value) return `Cadastrar ${model.copy.singular}`;
  if (isEdit.value) return `Editar ${model.copy.singular}`;
  return model.detail?.title || model.copy.plural;
});
const subtitle = computed(() => {
  if (isCreate.value) {
    return `Preencha os dados disponíveis para criar ${model.copy.singular}.`;
  }
  return model.detail?.subtitle || model.copy.subtitle;
});
const status = computed(() => model.detail?.status || 'Sem status');
const activeStep = computed(() => {
  const value = status.value.toLocaleLowerCase('pt-BR');
  if (/final|conclu|aceit|pago|ativo|encerr/.test(value)) return 3;
  if (/contrat|assin|concili|disponib|visit/.test(value)) return 2;
  if (/docum|confirm|confer|agenda/.test(value)) return 1;
  return 0;
});
const primaryImage = computed(() => {
  const record = source.value;
  const firstImage = asArray(record.imovel?.images || record.property?.images)[0] || {};
  return firstText(
    record.image_url,
    record.cover_url,
    record.imovel?.image_url,
    record.imovel?.cover_url,
    record.property?.image_url,
    record.property?.cover_url,
    record.transaction?.imovel?.image_url,
    firstImage.url,
    firstImage.file_url,
    firstImage.thumb_url
  );
});
const heroLabel = computed(() =>
  firstText(
    source.value.reference,
    source.value.code,
    source.value.protocol,
    model.detail?.id ? `${model.copy.recordLabel} #${model.detail.id}` : ''
  )
);
const participants = computed(() => {
  const record = source.value;
  const candidates = [
    ...asArray(record.participants),
    ...asArray(record.beneficiaries),
    ...asArray(record.payment_responsibilities),
    ...asArray(record.parties),
  ];

  return candidates
    .map(participant => {
      const name = firstText(
        participant.name,
        participant.user?.name,
        participant.contact?.name,
        participant.beneficiary?.name,
        participant.beneficiary_name,
        participant.recipient?.name
      );
      if (!name) return null;

      return {
        avatar: firstText(
          participant.avatar_url,
          participant.user?.avatar_url,
          participant.contact?.avatar_url,
          participant.beneficiary?.avatar_url
        ),
        id: participant.id || name,
        initials: initialsFor(name),
        name,
        role: firstText(
          participant.role,
          participant.kind,
          participant.beneficiary_kind,
          participant.status,
          'Participante'
        ),
      };
    })
    .filter(Boolean)
    .slice(0, 5);
});
const detailPanels = computed(() =>
  presentation.value.panels.map((panelTitle, panelIndex) => ({
    facts: model.detailFacts.filter((_, factIndex) => factIndex % 3 === panelIndex),
    title: panelTitle,
  }))
);
const detailMetrics = computed(() =>
  model.metrics.slice(0, 3).map(metric => ({
    ...metric,
    value: metric.value || '—',
  }))
);
const journeyState = index => {
  if (index < activeStep.value) return 'Concluída';
  if (index === activeStep.value) return 'Etapa atual';
  return 'Pendente';
};
const journeyStateClass = index => {
  if (index < activeStep.value) return 'bg-emerald-100 text-emerald-700';
  if (index === activeStep.value) return 'bg-[#5a24c7] text-white';
  return 'border border-[#dfe2eb] bg-white text-[#8b94ab]';
};
const journey = computed(() =>
  presentation.value.stages.map((stage, index) => ({
    id: stage,
    label: stage,
    state: journeyState(index),
    stateClass: journeyStateClass(index),
  }))
);
const historyPreview = computed(() => model.history.slice(0, 6));
const canCounterOffer = computed(() =>
  model.actionOptions.some(action => action.name === 'counter')
);
const hasEditingExperience = computed(
  () =>
    model.canEdit ||
    model.actionOptions.length > 0 ||
    model.transitionOptions.length > 0
);
const createOptions = computed(() => realData.data.value?.createOptions || {});
const lookups = computed(() => {
  const groups = [
    {
      field: 'imovel_id',
      icon: 'i-lucide-building-2',
      label: 'Imóveis disponíveis',
      records: createOptions.value.properties || [],
    },
    {
      field: 'imovel_listing_id',
      icon: 'i-lucide-house',
      label: 'Anúncios disponíveis',
      records: createOptions.value.listings || [],
    },
    {
      field: 'property_transaction_id',
      icon: 'i-lucide-handshake',
      label: 'Vendas disponíveis',
      records: createOptions.value.transactions || [],
    },
    {
      field: 'contact_id',
      icon: 'i-lucide-user-round',
      label: 'Clientes disponíveis',
      records: createOptions.value.contacts || [],
    },
  ];

  return groups.filter(group => model.fields.some(field => field.key === group.field));
});
const recordName = record =>
  record?.title ||
  record?.titulo ||
  record?.reference ||
  record?.name ||
  record?.display_name ||
  `Registro #${record?.id}`;
const recordSubtitle = record =>
  record?.address?.title ||
  record?.endereco?.logradouro ||
  record?.email ||
  record?.status ||
  'Disponível na API';
const recordImage = record =>
  firstText(
    record?.image_url,
    record?.cover_url,
    record?.imovel?.image_url,
    record?.imovel?.cover_url,
    record?.property?.image_url,
    record?.property?.cover_url,
    asArray(record?.images)[0]?.url,
    asArray(record?.images)[0]?.file_url
  );
const lookupFieldLabel = computed(() =>
  lookups.value.map(group => group.label.replace(' disponíveis', '')).join(' e ')
);
const salesCreateQuery = ref('');
const salesPropertyLookups = computed(() =>
  lookups.value.filter(group => /imovel|listing/.test(group.field))
);
const salesParticipantLookups = computed(() =>
  lookups.value.filter(group => /contact|participant|client/.test(group.field))
);
const salesParticipantOptions = computed(() =>
  salesParticipantLookups.value.flatMap(group =>
    group.records.map(record => ({ field: group.field, record }))
  )
);
const salesPropertyOptions = computed(() =>
  salesPropertyLookups.value.flatMap(group =>
    group.records.map(record => ({ field: group.field, record }))
  )
);
const visibleSalesPropertyOptions = computed(() => {
  const query = salesCreateQuery.value.trim().toLocaleLowerCase('pt-BR');
  if (!query) return salesPropertyOptions.value;

  return salesPropertyOptions.value.filter(({ record }) =>
    `${recordName(record)} ${recordSubtitle(record)}`
      .toLocaleLowerCase('pt-BR')
      .includes(query)
  );
});
const salesCreateFields = computed(() =>
  model.fields.filter(
    field =>
      !salesPropertyLookups.value.some(group => group.field === field.key) &&
      !salesParticipantLookups.value.some(group => group.field === field.key)
  )
);
const salesOptionalFields = computed(() =>
  salesCreateFields.value.filter(field => !field.required)
);
const filledFields = computed(
  () => model.fields.filter(field => model.form[field.key]).length
);
const updateField = (key, event) => {
  model.form[key] = event?.target?.value ?? event;
};
const selectLookup = (field, record) => {
  model.form[field] = String(record.id);
  if (field === 'imovel_listing_id' && record.imovel_id) {
    model.form.imovel_id = String(record.imovel_id);
  }
};
const returnToDashboard = () => emit('navigate', 'dashboard');
const openDetail = () => emit('navigate', 'detail');
const openEdit = () => emit('navigate', 'edit');
const submit = async () => {
  if (!model.canSubmit) return;

  const response = isCreate.value ? await model.create() : await model.saveEdit();
  if (!response) return;

  const id = recordIdFromResponse(response);
  if (isCreate.value && id) {
    emit('navigate', { id, screen: 'detail' });
    return;
  }
  emit('navigate', isCreate.value ? 'dashboard' : 'detail');
};
</script>

<template>
  <main class="mx-auto w-full max-w-[1440px] px-5 pb-20 pt-4 text-[#101958]">
    <template v-if="isSalesEdit">
      <header data-imudei-visual-id="sales-edit-header" class="grid grid-cols-[minmax(0,1fr)_auto_auto] items-start gap-5">
        <div>
          <p class="text-[9px] font-semibold text-[#5a24c7]">Vendas <span class="mx-1 text-[#b3bad0]">/</span>{{ heroLabel || 'Venda' }} <span class="mx-1 text-[#b3bad0]">/</span>Editar</p>
          <h1 class="mt-3 text-[24px] font-bold tracking-[-0.035em]">Editar venda</h1>
          <p class="mt-2 text-[9px] text-[#69759c]">Status atual: <strong class="font-semibold text-[#4d5578]">{{ status }}</strong></p>
        </div>
        <div class="flex items-start gap-3 pt-2">
          <span v-for="participant in participants" :key="participant.id" class="grid justify-items-center gap-1 text-center">
            <img v-if="participant.avatar" :src="participant.avatar" :alt="participant.name" class="size-11 rounded-full border-2 border-white object-cover ring-2 ring-violet-200" />
            <i v-else class="grid size-11 place-items-center rounded-full bg-gradient-to-br from-[#4d1ab7] via-[#7955d8] to-[#e454bd] text-[9px] font-bold text-white ring-2 ring-violet-200">{{ participant.initials }}</i>
            <small class="max-w-14 truncate text-[7px] text-[#596581]">{{ participant.name }}</small>
          </span>
          <p v-if="!participants.length" class="pt-3 text-[8px] text-[#7883a2]">Participantes não retornados pela API.</p>
        </div>
        <article class="min-w-[270px] rounded-2xl border border-[#ece7f1] bg-white px-4 py-3 shadow-[0_10px_24px_rgba(69,36,147,0.06)]">
          <div class="flex items-start gap-3"><i class="grid size-9 place-items-center rounded-xl bg-violet-50 text-[#6530d8]"><i class="i-lucide-star size-4" /></i><span class="min-w-0"><small class="block text-[8px] font-bold">Missão do dia</small><strong class="mt-1 block text-[10px]">Conclua os dados disponíveis</strong><p class="mt-1 text-[8px] text-[#7883a2]">A API não retornou metas ou pontuação para esta venda.</p></span></div>
          <div class="mt-3 h-1.5 rounded-full bg-[#eeeaf4]"><span class="block h-full w-0 rounded-full bg-[#6d35db]" /></div>
        </article>
      </header>

      <section class="mt-5 grid grid-cols-[236px_minmax(0,1fr)_260px] gap-4">
        <aside data-imudei-visual-id="sales-edit-journey" class="space-y-3">
          <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4">
            <header class="flex items-center justify-between"><h2 class="text-[10px] font-bold">Jornada da venda</h2><small class="text-[8px] text-[#7883a2]">{{ activeStep + 1 }}/{{ journey.length }}</small></header>
            <div class="mt-3 h-1.5 rounded-full bg-[#eeeaf4]"><span class="block h-full rounded-full bg-gradient-to-r from-[#5b24cc] to-[#a44ce5]" :class="activeStep === 0 ? 'w-1/4' : activeStep === 1 ? 'w-2/4' : activeStep === 2 ? 'w-3/4' : 'w-full'" /></div>
            <ol class="mt-5 space-y-4">
              <li v-for="(stage, index) in journey" :key="stage.id" class="relative flex gap-3"><i v-if="index < journey.length - 1" class="absolute left-[11px] top-6 h-7 w-px bg-[#e7e1ef]" /><span class="z-10 grid size-6 shrink-0 place-items-center rounded-full text-[8px] font-bold" :class="stage.stateClass">{{ index < activeStep ? '✓' : index + 1 }}</span><span class="min-w-0"><strong class="block text-[8px]">{{ stage.label }}</strong><small class="mt-1 block text-[7px] text-[#7b86a1]">{{ stage.state }}</small></span></li>
            </ol>
            <div class="mt-5 rounded-xl border border-violet-100 bg-violet-50 px-3 py-3"><i class="i-lucide-gift size-4 text-[#6530d8]" /><strong class="ml-2 text-[8px] text-[#5a24c7]">Recompensa indisponível</strong><small class="mt-1 block text-[7px] text-[#7883a2]">Sem contrato de gamificação retornado.</small></div>
          </article>
          <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4"><header class="flex items-center justify-between"><h2 class="text-[10px] font-bold">Selos conquistados</h2><small class="text-[7px] text-[#7883a2]">Indisponíveis</small></header><div class="mt-4 flex gap-2"><i v-for="index in 4" :key="index" class="grid size-9 place-items-center rounded-full bg-[#f4f1f8] text-[#a58add]"><i class="i-lucide-lock-keyhole size-3.5" /></i></div></article>
        </aside>

        <form data-imudei-visual-id="sales-edit-form" class="min-w-0" @submit.prevent="submit">
          <article class="grid min-h-[112px] grid-cols-[162px_minmax(0,1fr)] overflow-hidden rounded-2xl border border-[#e8e4ef] bg-white">
            <div class="bg-[#f4efff] p-3">
              <img v-if="primaryImage" :src="primaryImage" :alt="model.detail?.title" class="h-full min-h-[86px] w-full rounded-xl object-cover" />
              <div v-else class="grid h-full min-h-[86px] place-items-center rounded-xl bg-gradient-to-br from-[#ede4ff] via-[#f7f3ff] to-[#fbf6fd] text-center text-[#6b37c8]"><i class="i-lucide-building-2 size-8" /><small class="mt-2 px-3 text-[7px] leading-3">Imagem do imóvel não disponibilizada pela API.</small></div>
            </div>
            <div class="min-w-0 p-4">
              <div class="flex items-start justify-between gap-3"><span class="min-w-0"><h2 class="truncate text-[15px] font-bold">{{ model.detail?.title || 'Venda não retornada pela API' }}</h2><p class="mt-1 truncate text-[9px] text-[#6f799d]">{{ model.detail?.subtitle || 'Sem localização retornada' }}</p></span><span class="rounded-full px-2 py-1 text-[7px] font-bold" :class="statusTone(status)">{{ status }}</span></div>
              <div class="mt-4 flex flex-wrap gap-2"><span v-for="fact in model.detailFacts.slice(0, 4)" :key="fact.label" class="rounded-lg border border-[#eeeaf4] px-2.5 py-1.5 text-[8px]"><small class="text-[#8490ad]">{{ fact.label }}</small><strong class="ml-1 text-[#4d5578]">{{ fact.value }}</strong></span></div>
            </div>
          </article>

          <section class="mt-3 rounded-2xl border border-[#e8e4ef] bg-white p-4">
            <header class="flex items-center justify-between"><span><h2 class="text-[13px] font-bold">Negociação</h2><p class="mt-1 text-[8px] text-[#7b86a1]">Campos e transições só ficam disponíveis quando a API e a permissão autorizam.</p></span><span class="rounded-lg bg-violet-50 px-2 py-1 text-[7px] font-bold text-[#5a24c7]">Dados reais</span></header>
            <div v-if="model.canEdit && model.editableFields.length" class="mt-4 grid grid-cols-3 gap-x-3 gap-y-4">
              <label v-for="field in model.editableFields" :key="field.key" class="block text-[8px] font-semibold" :class="field.type === 'textarea' ? 'col-span-3' : ''">{{ field.label }}<span v-if="field.required" class="text-rose-500"> *</span><textarea v-if="field.type === 'textarea'" :value="model.form[field.key]" class="mt-2 min-h-[72px] w-full rounded-xl border border-[#e3deea] px-3 py-2 text-[9px] font-normal outline-none focus:border-[#805ce1]" :required="field.required" @input="updateField(field.key, $event)" /><select v-else-if="field.type === 'select'" :value="model.form[field.key]" class="mt-2 h-10 w-full rounded-xl border border-[#e3deea] bg-white px-3 text-[9px] font-normal outline-none focus:border-[#805ce1]" :required="field.required" @change="updateField(field.key, $event)"><option value="">Selecione</option><option v-for="option in field.options || []" :key="option" :value="option">{{ option }}</option></select><input v-else :type="field.type || 'text'" :value="model.form[field.key]" class="mt-2 h-10 w-full rounded-xl border border-[#e3deea] px-3 text-[9px] font-normal outline-none focus:border-[#805ce1]" :required="field.required" @input="updateField(field.key, $event)" /></label>
            </div>
            <div v-else class="mt-4 grid grid-cols-2 gap-3"><article v-for="fact in model.detailFacts" :key="fact.label" class="rounded-xl border border-[#f0edf4] bg-[#fcfbfe] px-3 py-3"><small class="block text-[7px] text-[#8490ad]">{{ fact.label }}</small><strong class="mt-1 block text-[9px]">{{ fact.value }}</strong></article><p class="col-span-2 rounded-xl bg-[#faf8fc] px-3 py-3 text-[8px] text-[#7883a2]">Esta venda não retornou campos editáveis para o contrato atual.</p></div>
            <section v-if="model.actionOptions.length || model.transitionOptions.length" class="mt-4 border-t border-[#eeeaf4] pt-4"><label class="block text-[8px] font-semibold">Motivo ou observação<textarea v-model="model.form.reason" class="mt-2 min-h-[74px] w-full rounded-xl border border-[#e3deea] px-3 py-2 text-[9px] font-normal outline-none focus:border-[#805ce1]" placeholder="Descreva somente a justificativa necessária para a ação." /></label><div class="mt-3 flex flex-wrap gap-2"><button v-for="action in model.actionOptions" :key="action.name" class="rounded-lg border border-violet-200 bg-white px-3 py-2 text-[8px] font-semibold text-[#5a24c7] disabled:cursor-not-allowed disabled:opacity-50" type="button" :disabled="saving" @click="model.runAction(action.name)">{{ action.label }}</button><ImudeiRuntimeActionPanel v-if="model.transitionOptions.length" class="basis-full" :action-options="[]" :can-counter-offer="false" :form="model.form" :saving="saving" :transition-options="model.transitionOptions" @run="model.runAction" @update-field="updateField" /></div></section>
            <p v-if="model.unavailableMessage" class="mt-4 rounded-xl border border-violet-100 bg-violet-50 px-3 py-2 text-[8px] text-[#6540a8]">{{ model.unavailableMessage }}</p>
            <p v-if="model.localError || error" class="mt-4 rounded-xl border border-rose-100 bg-rose-50 px-3 py-2 text-[8px] text-rose-600">{{ model.localError || error }}</p>
            <p v-if="model.notice" class="mt-4 rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2 text-[8px] text-emerald-700">{{ model.notice }}</p>
          </section>

          <section class="mt-3 rounded-2xl border border-[#e8e4ef] bg-white p-4"><header class="flex items-center justify-between"><h2 class="text-[12px] font-bold">Participantes da venda</h2><small class="text-[8px] text-[#7883a2]">{{ participants.length }} retornado(s)</small></header><div v-if="participants.length" class="mt-3 divide-y divide-[#eeeaf4]"><article v-for="participant in participants" :key="participant.id" class="flex items-center gap-3 py-2.5"><img v-if="participant.avatar" :src="participant.avatar" :alt="participant.name" class="size-8 rounded-full object-cover" /><i v-else class="grid size-8 place-items-center rounded-full bg-violet-100 text-[8px] font-bold text-[#5a24c7]">{{ participant.initials }}</i><span class="min-w-0 flex-1"><strong class="block truncate text-[9px]">{{ participant.name }}</strong><small class="mt-1 block truncate text-[8px] text-[#7883a2]">{{ participant.role }}</small></span></article></div><p v-else class="mt-3 rounded-xl bg-[#faf8fc] px-3 py-3 text-[8px] text-[#7883a2]">A API não retornou participantes colaborativos para esta venda.</p></section>
        </form>

        <aside data-imudei-visual-id="sales-edit-sidebar" class="space-y-3">
          <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4"><h2 class="text-[10px] font-bold">Resumo das alterações</h2><dl class="mt-3 divide-y divide-[#eeeaf4]"><div v-for="fact in model.detailFacts" :key="fact.label" class="py-3"><dt class="text-[7px] text-[#8490ad]">{{ fact.label }}</dt><dd class="mt-1 text-[8px] font-semibold text-[#4d5578]">{{ fact.value }}</dd></div><div class="py-3"><dt class="text-[7px] text-[#8490ad]">Status atual</dt><dd class="mt-1"><span class="rounded-full px-2 py-1 text-[7px] font-bold" :class="statusTone(status)">{{ status }}</span></dd></div></dl></article>
          <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4"><header class="flex items-center justify-between"><h2 class="text-[10px] font-bold">Atividade recente</h2><small class="text-[7px] text-[#5a24c7]">{{ historyPreview.length }} evento(s)</small></header><div v-if="historyPreview.length" class="mt-3 space-y-3"><article v-for="item in historyPreview.slice(0, 3)" :key="item.id" class="flex gap-2"><i class="grid size-7 shrink-0 place-items-center rounded-full bg-violet-100 text-[#5a24c7]"><i class="i-lucide-history size-3" /></i><span class="min-w-0"><strong class="block truncate text-[8px]">{{ item.label }}</strong><small class="mt-1 block truncate text-[7px] text-[#8490ad]">{{ item.date || 'Sem data retornada' }}</small></span></article></div><p v-else class="mt-3 text-[8px] leading-4 text-[#7883a2]">Nenhuma atividade real foi retornada para esta venda.</p></article>
          <article class="rounded-2xl border border-violet-100 bg-gradient-to-br from-white to-[#fff2fc] p-4"><h2 class="text-[10px] font-bold">Gamificação</h2><p class="mt-2 text-[8px] leading-4 text-[#7883a2]">Pontuação e recompensa só aparecem quando retornadas pelo backend.</p></article>
        </aside>
      </section>

      <footer data-imudei-visual-id="sales-edit-footer" class="sticky bottom-3 z-10 mt-4 ml-[252px] flex items-center justify-end gap-3 rounded-2xl border border-[#eeeaf4] bg-white/95 px-4 py-3 shadow-[0_10px_26px_rgba(53,31,110,0.08)] backdrop-blur">
        <button class="rounded-xl border border-[#e5e0ec] bg-white px-5 py-2.5 text-[9px] font-semibold text-[#4d5578]" type="button" @click="openDetail">Cancelar</button>
        <button v-if="model.actionOptions.some(action => action.name === 'saveDraft')" class="rounded-xl border border-violet-100 bg-white px-5 py-2.5 text-[9px] font-semibold text-[#5a24c7] disabled:cursor-not-allowed disabled:opacity-50" type="button" :disabled="saving" @click="model.runAction('saveDraft')">Salvar rascunho</button>
        <button class="rounded-xl bg-gradient-to-r from-[#4d1ab7] to-[#c82ecf] px-6 py-2.5 text-[9px] font-bold text-white shadow-[0_7px_18px_rgba(94,41,187,0.2)] disabled:cursor-not-allowed disabled:opacity-50" type="button" :disabled="saving || !model.canSubmit" @click="submit">{{ saving ? 'Salvando…' : 'Salvar alterações' }}</button>
      </footer>
    </template>

    <template v-else-if="isSalesCreate">
      <header data-imudei-visual-id="sales-create-header" class="flex items-start justify-between gap-6">
        <div>
          <p class="text-[9px] font-semibold text-[#5a24c7]">Vendas <span class="mx-1 text-[#b3bad0]">/</span>Nova venda</p>
          <h1 class="mt-3 text-[24px] font-bold tracking-[-0.035em]">Cadastrar venda</h1>
          <p class="mt-2 text-[9px] text-[#69759c]">Os dados serão enviados apenas ao confirmar a criação.</p>
        </div>
        <article class="min-w-[310px] rounded-2xl border border-[#ece7f1] bg-white px-4 py-3 shadow-[0_10px_24px_rgba(69,36,147,0.06)]">
          <div class="flex items-start gap-3"><i class="grid size-9 place-items-center rounded-xl bg-violet-50 text-[#6530d8]"><i class="i-lucide-target size-4" /></i><span><small class="block text-[8px] font-bold">Missão do dia</small><strong class="mt-1 block text-[10px]">Complete os dados disponíveis</strong><p class="mt-1 text-[8px] text-[#7883a2]">Metas, bônus e pontos não foram retornados pela API.</p></span></div>
          <div class="mt-3 h-1.5 rounded-full bg-[#eeeaf4]"><span class="block h-full w-0 rounded-full bg-[#6d35db]" /></div>
        </article>
      </header>

      <section class="mt-4 grid grid-cols-[minmax(0,1fr)_340px] items-start gap-4">
        <div class="min-w-0">
          <nav data-imudei-visual-id="sales-create-steps" class="rounded-2xl border border-[#e8e4ef] bg-white px-5 py-4">
            <ol class="grid grid-cols-4 gap-3">
              <li v-for="(stage, index) in journey" :key="stage.id" class="relative flex min-w-0 items-center gap-3"><i v-if="index" class="absolute -left-3 top-4 h-px w-3 bg-[#e7e1ef]" /><span class="z-10 grid size-9 shrink-0 place-items-center rounded-full text-[11px] font-bold" :class="index === 0 ? 'bg-gradient-to-br from-[#4d1ab7] to-[#c82ecf] text-white shadow-[0_6px_14px_rgba(101,44,201,0.26)]' : 'bg-[#f2eff6] text-[#69759c]'">{{ index + 1 }}</span><span class="min-w-0"><strong class="block truncate text-[9px]">{{ index === 0 ? 'Imóvel' : stage.label }}</strong><small class="mt-1 block truncate text-[7px] text-[#8490ad]">{{ index === 0 ? 'Selecione o anúncio' : stage.state }}</small></span></li>
            </ol>
          </nav>

          <section data-imudei-visual-id="sales-create-catalog" class="mt-4">
            <div class="flex flex-wrap items-center gap-3">
              <div v-if="salesParticipantOptions.length" class="flex max-w-full items-center gap-2 overflow-x-auto pb-1"><button v-for="option in salesParticipantOptions" :key="`${option.field}-${option.record.id}`" class="flex h-11 shrink-0 items-center gap-2 rounded-xl border bg-white px-3 text-left" :class="String(model.form[option.field]) === String(option.record.id) ? 'border-[#7b48ed] bg-violet-50' : 'border-[#e9e3f0]'" type="button" @click="selectLookup(option.field, option.record)"><i class="grid size-6 place-items-center rounded-full bg-violet-100 text-[7px] font-bold text-[#5a24c7]">{{ initialsFor(recordName(option.record)) }}</i><span><strong class="block max-w-24 truncate text-[8px]">{{ recordName(option.record) }}</strong><small class="block text-[7px] text-[#7883a2]">Participante</small></span></button></div>
              <p v-else class="rounded-xl border border-dashed border-[#e8e4ef] bg-white px-3 py-2 text-[8px] text-[#7883a2]">Participantes não estão disponíveis no contrato de criação desta venda.</p>
            </div>

            <header class="mt-4 flex items-end justify-between gap-4"><span><h2 class="text-[15px] font-bold">1. Selecione o imóvel</h2><p class="mt-1 text-[8px] text-[#7b86a1]">Escolha apenas anúncios retornados pelo catálogo real da conta.</p></span><small class="rounded-full bg-violet-50 px-3 py-1.5 text-[8px] font-semibold text-[#5a24c7]">{{ salesPropertyOptions.length }} anúncio(s)</small></header>
            <label class="mt-3 flex h-10 items-center gap-3 rounded-xl border border-[#e5e0ec] bg-white px-3 text-[#7883a2]"><i class="i-lucide-search size-4" /><input v-model="salesCreateQuery" class="min-w-0 flex-1 bg-transparent text-[9px] text-[#4d5578] outline-none placeholder:text-[#8490ad]" placeholder="Buscar imóvel por código, endereço ou proprietário" type="search" aria-label="Buscar imóveis" /></label>

            <div v-if="visibleSalesPropertyOptions.length" class="mt-3 space-y-2">
              <button v-for="option in visibleSalesPropertyOptions" :key="`${option.field}-${option.record.id}`" class="grid w-full grid-cols-[100px_minmax(0,1fr)_auto] items-center gap-4 rounded-xl border bg-white p-2.5 text-left transition" :class="String(model.form[option.field]) === String(option.record.id) ? 'border-[#7b48ed] bg-violet-50/40 ring-1 ring-violet-200' : 'border-[#ebe7f1] hover:border-[#cdb9f6]'" type="button" @click="selectLookup(option.field, option.record)">
                <div class="grid h-14 place-items-center overflow-hidden rounded-lg bg-[#f2edff] text-[#6732d1]"><img v-if="recordImage(option.record)" :src="recordImage(option.record)" :alt="recordName(option.record)" class="h-full w-full object-cover" /><i v-else class="i-lucide-building-2 size-6" /></div>
                <span class="min-w-0"><strong class="block truncate text-[10px]">{{ recordName(option.record) }}</strong><small class="mt-1 block truncate text-[8px] text-[#6f799d]">{{ recordSubtitle(option.record) }}</small><small class="mt-2 block text-[7px] font-semibold text-[#5a24c7]">{{ String(model.form[option.field]) === String(option.record.id) ? 'Selecionado' : 'Disponível no catálogo' }}</small></span>
                <i class="size-5 rounded-full border" :class="String(model.form[option.field]) === String(option.record.id) ? 'i-lucide-circle-dot border-[#6530d8] text-[#6530d8]' : 'i-lucide-circle border-[#ddd7ea] text-transparent'" />
              </button>
            </div>
            <div v-else class="mt-3 grid min-h-[250px] place-items-center rounded-2xl border border-dashed border-[#ded3f4] bg-white text-center"><span class="max-w-[310px] px-5"><i class="i-lucide-house-x mx-auto size-8 text-[#a58add]" /><strong class="mt-3 block text-[10px]">Nenhum anúncio elegível foi retornado</strong><small class="mt-2 block text-[8px] leading-4 text-[#7883a2]">A criação permanece indisponível até a API retornar um anúncio compatível.</small></span></div>
            <div class="mt-3 rounded-xl border border-dashed border-[#ded3f4] bg-white px-4 py-3"><span class="flex items-center gap-3"><i class="grid size-8 place-items-center rounded-full bg-violet-50 text-[#5a24c7]"><i class="i-lucide-plus size-4" /></i><span><strong class="block text-[9px]">Cadastro manual indisponível</strong><small class="mt-1 block text-[8px] text-[#7883a2]">A API não expôs uma ação de criação manual de imóvel nesta etapa.</small></span></span></div>
          </section>

          <section v-if="salesOptionalFields.length" class="mt-3 rounded-2xl border border-[#e8e4ef] bg-white p-4"><header><h2 class="text-[10px] font-bold">Dados complementares</h2><p class="mt-1 text-[8px] text-[#7883a2]">Campos opcionais do contrato de criação.</p></header><div class="mt-3 grid grid-cols-2 gap-3"><label v-for="field in salesOptionalFields" :key="field.key" class="block text-[8px] font-semibold">{{ field.label }}<textarea v-if="field.type === 'textarea'" :value="model.form[field.key]" class="mt-2 min-h-[72px] w-full rounded-xl border border-[#e3deea] px-3 py-2 text-[9px] font-normal outline-none focus:border-[#805ce1]" @input="updateField(field.key, $event)" /><input v-else :type="field.type || 'text'" :value="model.form[field.key]" class="mt-2 h-10 w-full rounded-xl border border-[#e3deea] px-3 text-[9px] font-normal outline-none focus:border-[#805ce1]" @input="updateField(field.key, $event)" /></label></div></section>
          <p v-if="model.unavailableMessage" class="mt-3 rounded-xl border border-violet-100 bg-violet-50 px-4 py-3 text-[8px] text-[#6540a8]">{{ model.unavailableMessage }}</p>
          <p v-if="model.localError || error" class="mt-3 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-[8px] text-rose-600">{{ model.localError || error }}</p>
          <p v-if="model.notice" class="mt-3 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-[8px] text-emerald-700">{{ model.notice }}</p>
        </div>

        <aside data-imudei-visual-id="sales-create-sidebar" class="space-y-3">
          <article class="rounded-2xl border border-[#eadff9] bg-gradient-to-br from-white to-[#fff4fc] p-4"><header class="flex items-center gap-3"><i class="grid size-9 place-items-center rounded-xl bg-violet-50 text-[#6530d8]"><i class="i-lucide-target size-4" /></i><span><h2 class="text-[11px] font-bold">Missão do dia</h2><p class="mt-1 text-[8px] text-[#69759c]">Conclua o cadastro com dados reais.</p></span></header><p class="mt-4 text-[8px] text-[#7883a2]">Pontuação e bônus indisponíveis sem retorno do backend.</p><div class="mt-4 h-1.5 rounded-full bg-[#ebe6f4]"><span class="block h-full w-0 rounded-full bg-[#5a24c7]" /></div><small class="mt-2 block text-right text-[8px] text-[#8490ad]">{{ filledFields }}/{{ model.fields.length }} campos</small></article>
          <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4"><header class="flex items-center justify-between"><h2 class="text-[10px] font-bold">Sua jornada de venda</h2><small class="text-[7px] text-[#7883a2]">{{ activeStep + 1 }} de {{ journey.length }}</small></header><div class="mt-4 flex items-start justify-between"><span v-for="(stage, index) in journey" :key="stage.id" class="grid justify-items-center gap-2 text-center"><i class="grid size-8 place-items-center rounded-full text-[9px] font-bold" :class="index === 0 ? 'bg-[#5a24c7] text-white' : 'bg-[#f0edf6] text-[#8b94ab]'">{{ index + 1 }}</i><small class="max-w-12 text-[7px] text-[#69759c]">{{ index === 0 ? 'Imóvel' : stage.label }}</small></span></div></article>
          <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4"><header class="flex items-center justify-between"><h2 class="text-[10px] font-bold">Suas conquistas</h2><small class="text-[7px] text-[#5a24c7]">Indisponíveis</small></header><div class="mt-4 grid grid-cols-4 gap-2"><span v-for="index in 4" :key="index" class="grid justify-items-center gap-2"><i class="grid size-10 place-items-center rounded-full bg-[#f4f1f8] text-[#a58add]"><i class="i-lucide-lock-keyhole size-4" /></i><small class="text-[7px] text-[#7883a2]">—</small></span></div></article>
          <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4"><header class="flex items-center justify-between"><h2 class="text-[10px] font-bold">Atividade recente</h2><i class="i-lucide-history size-3.5 text-[#5a24c7]" /></header><p class="mt-3 text-[8px] leading-4 text-[#7883a2]">A atividade só será exibida depois que uma venda real for criada.</p></article>
        </aside>
      </section>

      <footer data-imudei-visual-id="sales-create-footer" class="sticky bottom-3 z-10 mt-4 flex items-center justify-between rounded-2xl border border-[#eeeaf4] bg-white/95 px-4 py-3 shadow-[0_10px_26px_rgba(53,31,110,0.08)] backdrop-blur"><div class="flex items-center gap-3"><button class="rounded-xl border border-violet-200 bg-white px-4 py-2.5 text-[9px] font-semibold text-[#5a24c7]" type="button" disabled>Rascunho indisponível</button><small class="text-[8px] text-[#7883a2]">Não há ação de rascunho retornada pelo backend.</small></div><div class="flex items-center gap-3"><button class="rounded-xl border border-[#e5e0ec] bg-white px-5 py-2.5 text-[9px] font-semibold text-[#4d5578]" type="button" @click="returnToDashboard">Cancelar</button><button class="rounded-xl bg-gradient-to-r from-[#4d1ab7] to-[#c82ecf] px-6 py-2.5 text-[9px] font-bold text-white shadow-[0_7px_18px_rgba(94,41,187,0.2)] disabled:cursor-not-allowed disabled:opacity-50" type="button" :disabled="saving || !model.canCreate" @click="submit">{{ saving ? 'Criando…' : 'Continuar' }}</button></div></footer>
    </template>

    <template v-else>
    <header class="flex flex-wrap items-start justify-between gap-4">
      <div>
        <p class="text-[9px] font-semibold text-[#5a24c7]">
          {{ model.copy.plural }} <span class="mx-1 text-[#b3bad0]">/</span>
          {{ isCreate ? 'Novo cadastro' : hasDetail ? heroLabel : 'Operação' }}
        </p>
        <div class="mt-1.5 flex flex-wrap items-center gap-3">
          <h1 class="text-[24px] font-bold tracking-[-0.035em]">{{ title }}</h1>
          <span
            v-if="!isCreate && hasDetail"
            class="rounded-full px-2.5 py-1 text-[8px] font-bold"
            :class="statusTone(status)"
          >
            {{ status }}
          </span>
        </div>
        <p class="mt-1 text-[10px] text-[#69759c]">{{ subtitle }}</p>
      </div>
      <div class="flex items-center gap-2">
        <button
          v-if="!isCreate && hasDetail"
          class="grid size-9 place-items-center rounded-xl border border-[#e5e0ec] bg-white text-[#5a24c7]"
          type="button"
          aria-label="Atualizar dados"
          @click="realData.refresh"
        >
          <i class="i-lucide-refresh-cw size-4" />
        </button>
        <button
          v-if="isDetail && hasEditingExperience"
          class="flex h-9 items-center gap-2 rounded-xl border border-[#d9caee] bg-white px-3 text-[9px] font-semibold text-[#5520c4]"
          type="button"
          @click="openEdit"
        >
          <i class="i-lucide-pencil size-3.5" />Editar {{ model.copy.singular }}
        </button>
        <button
          v-if="!isDetail"
          class="flex h-9 items-center gap-2 rounded-xl border border-[#e5e0ec] bg-white px-3 text-[9px] font-semibold text-[#4d5578]"
          type="button"
          @click="isCreate ? returnToDashboard() : openDetail()"
        >
          Cancelar
        </button>
      </div>
    </header>

    <template v-if="isDetail && hasDetail">
      <section class="mt-4 overflow-hidden rounded-2xl border border-[#e8e4ef] bg-white">
        <div class="grid grid-cols-[minmax(230px,0.88fr)_minmax(0,1.35fr)_minmax(360px,1.35fr)]">
          <div class="min-h-[160px] bg-[#f4efff] p-3">
            <img
              v-if="primaryImage"
              :src="primaryImage"
              :alt="model.detail.title"
              class="h-full min-h-[136px] w-full rounded-xl object-cover"
            />
            <div
              v-else
              class="grid h-full min-h-[136px] place-items-center rounded-xl bg-gradient-to-br from-[#ede4ff] via-[#f7f3ff] to-[#fbf6fd] text-[#6b37c8]"
            >
              <i :class="presentation.icon" class="size-9" />
            </div>
          </div>
          <div class="flex min-w-0 flex-col justify-center border-l border-[#eeeaf4] px-5 py-4">
            <span class="text-[8px] font-bold uppercase tracking-[0.14em] text-[#8b65cf]">{{ model.copy.recordLabel }}</span>
            <h2 class="mt-2 truncate text-[16px] font-bold tracking-[-0.025em]">{{ model.detail.title }}</h2>
            <p class="mt-1 truncate text-[9px] text-[#6f799d]">{{ model.detail.subtitle }}</p>
            <div class="mt-4 flex items-center gap-2">
              <span class="rounded-full px-2 py-1 text-[7px] font-bold" :class="statusTone(status)">{{ status }}</span>
              <span class="truncate text-[8px] text-[#7b86a1]">{{ heroLabel }}</span>
            </div>
          </div>
          <div class="grid grid-cols-3 divide-x divide-[#eeeaf4] border-l border-[#eeeaf4]">
            <article v-for="metric in detailMetrics" :key="metric.label" class="min-w-0 px-4 py-5">
              <span :class="metric.icon" class="size-4 text-[#6d38cf]" />
              <small class="mt-3 block text-[7px] font-semibold uppercase tracking-[0.1em] text-[#8490ad]">{{ metric.label }}</small>
              <strong class="mt-1 block truncate text-[11px] text-[#5a24c7]">{{ metric.value }}</strong>
            </article>
          </div>
        </div>
        <div class="border-t border-[#eeeaf4] px-5 py-4">
          <div class="grid grid-cols-4 gap-3">
            <article v-for="(stage, index) in journey" :key="stage.id" class="relative flex min-w-0 items-center gap-2">
              <i v-if="index" class="absolute -left-3 right-[calc(100%-12px)] top-3 h-px bg-[#ddd7ea]" />
              <span class="z-10 grid size-6 shrink-0 place-items-center rounded-full text-[8px] font-bold" :class="stage.stateClass">
                {{ index < activeStep ? '✓' : index + 1 }}
              </span>
              <span class="min-w-0">
                <strong class="block truncate text-[8px]">{{ stage.label }}</strong>
                <small class="block truncate text-[7px] text-[#8490ad]">{{ stage.state }}</small>
              </span>
            </article>
          </div>
        </div>
      </section>

      <nav class="mt-3 flex items-center gap-5 border-b border-[#e8e4ef] px-2" aria-label="Seções da operação">
        <button
          v-for="(tab, index) in presentation.tabs"
          :key="tab"
          class="border-b-2 px-1 pb-2.5 text-[8px] font-semibold"
          :class="index === 0 ? 'border-[#5a24c7] text-[#5a24c7]' : 'border-transparent text-[#7b86a1]'"
          type="button"
        >
          {{ tab }}
        </button>
      </nav>

      <section class="mt-4 grid grid-cols-[minmax(0,1fr)_270px] gap-4">
        <div class="space-y-4">
          <section class="grid grid-cols-3 gap-3">
            <article v-for="panel in detailPanels" :key="panel.title" class="rounded-2xl border border-[#e8e4ef] bg-white p-4">
              <header class="flex items-center justify-between">
                <h2 class="text-[10px] font-bold">{{ panel.title }}</h2>
                <i class="i-lucide-pencil size-3 text-[#8a64ca]" />
              </header>
              <dl class="mt-4 space-y-3">
                <div v-for="fact in panel.facts" :key="fact.label" class="min-w-0">
                  <dt class="text-[7px] text-[#8490ad]">{{ fact.label }}</dt>
                  <dd class="mt-1 truncate text-[8px] font-semibold text-[#202958]">{{ fact.value || 'Não informado' }}</dd>
                </div>
                <p v-if="!panel.facts.length" class="text-[8px] leading-5 text-[#8490ad]">Nenhuma informação adicional foi retornada pelo backend.</p>
              </dl>
            </article>
          </section>

          <section class="rounded-2xl border border-[#e8e4ef] bg-white p-4">
            <header class="flex items-center justify-between">
              <div>
                <h2 class="text-[11px] font-bold">Linha do tempo da operação</h2>
                <p class="mt-1 text-[8px] text-[#7b86a1]">Acompanhe os marcos retornados no histórico real.</p>
              </div>
              <span class="text-[8px] font-semibold text-[#5a24c7]">{{ historyPreview.length }} eventos</span>
            </header>
            <div class="mt-4 grid grid-cols-4 gap-3">
              <article v-for="(stage, index) in journey" :key="stage.id" class="rounded-xl border border-[#eeeaf4] p-3">
                <span class="grid size-6 place-items-center rounded-full text-[8px] font-bold" :class="stage.stateClass">{{ index + 1 }}</span>
                <strong class="mt-3 block text-[8px]">{{ stage.label }}</strong>
                <small class="mt-1 block text-[7px] text-[#8490ad]">{{ stage.state }}</small>
              </article>
            </div>
          </section>

          <section class="grid grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] gap-4">
            <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4">
              <header class="flex items-center justify-between">
                <h2 class="text-[10px] font-bold">Participantes</h2>
                <span class="text-[8px] text-[#8a64ca]">{{ participants.length }} vinculados</span>
              </header>
              <div v-if="participants.length" class="mt-4 space-y-3">
                <div v-for="participant in participants" :key="participant.id" class="flex min-w-0 items-center gap-2">
                  <img v-if="participant.avatar" :src="participant.avatar" :alt="participant.name" class="size-8 rounded-full object-cover" />
                  <span v-else class="grid size-8 place-items-center rounded-full bg-[#eee6ff] text-[8px] font-bold text-[#5a24c7]">{{ participant.initials }}</span>
                  <span class="min-w-0"><strong class="block truncate text-[8px]">{{ participant.name }}</strong><small class="block truncate text-[7px] text-[#8490ad]">{{ participant.role }}</small></span>
                </div>
              </div>
              <p v-else class="mt-4 rounded-xl bg-[#faf8ff] p-3 text-[8px] leading-5 text-[#7b86a1]">Nenhum participante foi retornado pela API para esta operação.</p>
            </article>
            <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4">
              <header class="flex items-center justify-between"><h2 class="text-[10px] font-bold">Informações confirmadas</h2><i class="i-lucide-shield-check size-4 text-emerald-600" /></header>
              <div class="mt-4 grid grid-cols-2 gap-x-5 gap-y-4">
                <div v-for="fact in model.detailFacts.slice(0, 6)" :key="fact.label" class="min-w-0"><small class="block text-[7px] text-[#8490ad]">{{ fact.label }}</small><strong class="mt-1 block truncate text-[8px]">{{ fact.value || 'Não informado' }}</strong></div>
              </div>
            </article>
          </section>
        </div>

        <aside class="space-y-4">
          <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4">
            <header class="flex items-center justify-between"><h2 class="text-[10px] font-bold">Atividades recentes</h2><i class="i-lucide-filter size-3.5 text-[#7c61b8]" /></header>
            <div v-if="historyPreview.length" class="mt-4 space-y-4">
              <article v-for="item in historyPreview" :key="item.id" class="flex gap-2 border-b border-[#f0edf5] pb-3 last:border-0 last:pb-0">
                <span class="grid size-7 shrink-0 place-items-center rounded-full bg-[#f0eaff] text-[#5a24c7]"><i class="i-lucide-history size-3" /></span>
                <span class="min-w-0 flex-1"><strong class="block truncate text-[8px]">{{ item.label }}</strong><small class="mt-1 block truncate text-[7px] text-[#8490ad]">{{ item.date || 'Sem data disponível' }}</small></span>
              </article>
            </div>
            <p v-else class="mt-4 rounded-xl bg-[#faf8ff] p-3 text-[8px] leading-5 text-[#7b86a1]">O contrato atual não retornou histórico para esta operação.</p>
          </article>
          <article v-if="model.actionOptions.length" class="rounded-2xl border border-[#e8e4ef] bg-white p-4">
            <h2 class="text-[10px] font-bold">Ações da operação</h2>
            <div class="mt-4 space-y-2">
              <button v-for="action in model.actionOptions" :key="action.name" class="flex h-9 w-full items-center justify-between rounded-lg border border-[#dfd3f6] px-3 text-left text-[8px] font-semibold text-[#5a24c7]" type="button" :disabled="saving" @click="model.runAction(action.name)">
                {{ action.label }}<i class="i-lucide-arrow-right size-3.5" />
              </button>
            </div>
          </article>
          <article class="rounded-2xl border border-[#eadff9] bg-gradient-to-br from-white to-[#fff2fc] p-4">
            <h2 class="text-[10px] font-bold">Recompensas</h2>
            <p class="mt-3 text-[8px] leading-5 text-[#69759c]">Pontuação indisponível sem contrato de gamificação no backend.</p>
          </article>
        </aside>
      </section>
    </template>

    <template v-else>
      <section v-if="isEdit && hasDetail" class="mt-4 overflow-hidden rounded-2xl border border-[#e8e4ef] bg-white">
        <div class="grid grid-cols-[96px_minmax(0,1fr)_300px] items-center">
          <div class="grid h-[96px] place-items-center bg-[#f2edff] text-[#5a24c7]"><i :class="presentation.icon" class="size-7" /></div>
          <div class="min-w-0 px-4"><span class="text-[8px] font-bold uppercase tracking-[0.12em] text-[#9678cf]">{{ model.copy.recordLabel }}</span><h2 class="mt-1 truncate text-[12px] font-bold">{{ model.detail.title }}</h2><p class="mt-1 truncate text-[8px] text-[#7b86a1]">{{ model.detail.subtitle }}</p></div>
          <div class="grid grid-cols-3 divide-x divide-[#eeeaf4] border-l border-[#eeeaf4]">
            <span v-for="metric in detailMetrics" :key="metric.label" class="min-w-0 px-3 py-4"><small class="block text-[7px] text-[#8490ad]">{{ metric.label }}</small><strong class="mt-1 block truncate text-[8px] text-[#5a24c7]">{{ metric.value }}</strong></span>
          </div>
        </div>
      </section>

      <section class="mt-4 rounded-2xl border border-[#e8e4ef] bg-white px-4 py-3">
        <div class="grid grid-cols-4 gap-3">
          <article v-for="(stage, index) in journey" :key="stage.id" class="flex min-w-0 items-center gap-2">
            <span class="grid size-7 shrink-0 place-items-center rounded-full text-[8px] font-bold" :class="index === 0 || (isEdit && index <= activeStep) ? 'bg-[#5a24c7] text-white' : 'bg-[#f0edf6] text-[#8b94ab]'">{{ index + 1 }}</span>
            <span class="min-w-0"><strong class="block truncate text-[8px]" :class="index === 0 || (isEdit && index <= activeStep) ? 'text-[#5a24c7]' : ''">{{ stage.label }}</strong><small class="block truncate text-[7px] text-[#8490ad]">{{ index === 0 ? (isCreate ? 'Comece aqui' : 'Etapa atual') : 'Próxima etapa' }}</small></span>
          </article>
        </div>
      </section>

      <section class="mt-4 grid grid-cols-[208px_minmax(0,1fr)_260px] items-start gap-4">
        <aside class="space-y-3">
          <article class="overflow-hidden rounded-2xl border border-[#e8e4ef] bg-white p-2">
            <button v-for="(stage, index) in presentation.stages" :key="stage" class="flex min-h-[58px] w-full items-center gap-3 rounded-xl px-3 text-left" :class="index === 0 ? 'bg-[#f5f0ff] text-[#5a24c7]' : 'text-[#516084]'" type="button">
              <span class="grid size-7 place-items-center rounded-full text-[8px] font-bold" :class="index === 0 ? 'bg-[#5a24c7] text-white' : 'bg-[#f0edf6] text-[#8b94ab]'">{{ index + 1 }}</span>
              <span class="min-w-0"><strong class="block truncate text-[8px]">{{ stage }}</strong><small class="mt-0.5 block truncate text-[7px] text-[#8490ad]">{{ index === 0 ? 'Dados da operação' : 'A seguir' }}</small></span>
            </button>
          </article>
          <article class="rounded-2xl border border-[#eadff9] bg-gradient-to-br from-white to-[#fff4fc] p-4">
            <div class="flex items-center justify-between"><h2 class="text-[9px] font-bold">Progresso do cadastro</h2><span class="text-[8px] font-semibold text-[#5a24c7]">{{ filledFields }}/{{ model.fields.length }}</span></div>
            <div class="mt-3 h-1.5 overflow-hidden rounded-full bg-[#ebe6f4]"><i class="block h-full rounded-full bg-gradient-to-r from-[#5a24c7] to-[#d43bc7]" :class="filledFields ? 'w-1/2' : 'w-0'" /></div>
            <p class="mt-3 text-[8px] leading-5 text-[#7580a0]">Apenas campos já implementados no backend são exibidos nesta etapa.</p>
          </article>
        </aside>

        <form class="rounded-2xl border border-[#e8e4ef] bg-white p-5" @submit.prevent="submit">
          <header class="flex items-start justify-between gap-4">
            <div><h2 class="text-[13px] font-bold">{{ isCreate ? 'Dados disponíveis para cadastro' : 'Atualização operacional' }}</h2><p class="mt-1 text-[8px] text-[#7b86a1]">{{ isCreate ? `Selecione ${lookupFieldLabel || 'os vínculos'} e informe os campos obrigatórios.` : 'Altere somente os dados permitidos pelo contrato atual.' }}</p></div>
            <span class="grid size-8 place-items-center rounded-xl bg-[#f2edff] text-[#5a24c7]"><i :class="presentation.icon" class="size-4" /></span>
          </header>

          <section v-if="isCreate && lookups.length" class="mt-5 space-y-4">
            <article v-for="group in lookups" :key="group.field" class="rounded-xl border border-[#ece8f4] p-4">
              <header class="flex items-center justify-between"><span class="flex items-center gap-2"><i :class="group.icon" class="size-4 text-[#6c38ca]" /><span><strong class="block text-[9px]">{{ group.label }}</strong><small class="block text-[7px] text-[#8490ad]">Escolha um registro retornado pela API</small></span></span><span class="text-[8px] font-semibold text-[#5a24c7]">{{ group.records.length }} disponíveis</span></header>
              <div v-if="group.records.length" class="mt-4 grid grid-cols-2 gap-3">
                <button v-for="record in group.records.slice(0, 6)" :key="record.id" class="rounded-xl border p-3 text-left" :class="String(model.form[group.field]) === String(record.id) ? 'border-[#6d38cf] bg-[#faf7ff] ring-1 ring-[#d8c4fa]' : 'border-[#e9e5ef] bg-white'" type="button" @click="selectLookup(group.field, record)">
                  <span class="flex items-center gap-2"><span class="grid size-7 place-items-center rounded-lg bg-[#f1ebff] text-[#5a24c7]"><i :class="group.icon" class="size-3.5" /></span><span class="min-w-0"><b class="block truncate text-[8px]">{{ recordName(record) }}</b><small class="mt-1 block truncate text-[7px] text-[#7b86a1]">{{ recordSubtitle(record) }}</small></span></span>
                </button>
              </div>
              <p v-else class="mt-4 rounded-xl bg-[#fbfaff] p-3 text-[8px] leading-5 text-[#7b86a1]">Nenhum registro está disponível neste momento. O cadastro não será simulado.</p>
            </article>
          </section>

          <section v-if="model.fields.length" class="mt-5 border-t border-[#eeeaf4] pt-5">
            <header><h3 class="text-[10px] font-bold">Informações da operação</h3><p class="mt-1 text-[8px] text-[#7b86a1]">Os campos abaixo são enviados ao contrato real do backend.</p></header>
            <div class="mt-4 grid grid-cols-2 gap-4">
              <label v-for="field in model.fields" :key="field.key" class="block text-[8px] font-semibold" :class="field.type === 'textarea' ? 'col-span-2' : ''">
                {{ field.label }}<span v-if="field.required" class="text-rose-500"> *</span>
                <textarea v-if="field.type === 'textarea'" :value="model.form[field.key]" class="mt-2 min-h-[104px] w-full rounded-xl border border-[#e3deea] px-3 py-2.5 text-[9px] font-normal outline-none focus:border-[#805ce1]" :required="field.required" @input="updateField(field.key, $event)" />
                <select v-else-if="field.type === 'select'" :value="model.form[field.key]" class="mt-2 h-10 w-full rounded-xl border border-[#e3deea] bg-white px-3 text-[9px] font-normal outline-none focus:border-[#805ce1]" :required="field.required" @change="updateField(field.key, $event)">
                  <option value="">Selecione</option><option v-for="option in field.options || []" :key="option" :value="option">{{ option }}</option>
                </select>
                <input v-else :type="field.type || 'text'" :value="model.form[field.key]" class="mt-2 h-10 w-full rounded-xl border border-[#e3deea] px-3 text-[9px] font-normal outline-none focus:border-[#805ce1]" :required="field.required" @input="updateField(field.key, $event)" />
              </label>
            </div>
          </section>

          <section v-if="isEdit && (model.actionOptions.length || model.transitionOptions.length)" class="mt-5">
            <ImudeiRuntimeActionPanel :action-options="model.actionOptions" :can-counter-offer="canCounterOffer" :form="model.form" :saving="saving" :transition-options="model.transitionOptions" @run="model.runAction" @update-field="updateField" />
          </section>

          <p v-if="model.unavailableMessage" class="mt-5 rounded-xl border border-violet-100 bg-violet-50 px-4 py-3 text-[8px] text-[#6540a8]">{{ model.unavailableMessage }}</p>
          <p v-if="model.localError || error" class="mt-5 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-[8px] text-rose-600">{{ model.localError || error }}</p>
          <p v-if="model.notice" class="mt-5 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-[8px] text-emerald-700">{{ model.notice }}</p>

          <footer class="mt-6 flex items-center justify-between border-t border-[#eeeaf4] pt-5">
            <button class="rounded-xl border border-[#e5e0ec] bg-white px-5 py-2.5 text-[9px] font-semibold text-[#4d5578]" type="button" @click="isCreate ? returnToDashboard() : openDetail()">Cancelar</button>
            <button v-if="model.canSubmit" class="rounded-xl bg-gradient-to-r from-[#4d1ab7] to-[#c82ecf] px-5 py-2.5 text-[9px] font-bold text-white shadow-[0_7px_18px_rgba(94,41,187,0.2)] disabled:cursor-not-allowed disabled:opacity-50" type="submit" :disabled="saving">{{ saving ? 'Salvando…' : presentation.action }}</button>
          </footer>
        </form>

        <aside class="space-y-4">
          <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4"><h2 class="text-[10px] font-bold">Resumo {{ isCreate ? 'do cadastro' : 'das alterações' }}</h2><dl class="mt-4 divide-y divide-[#eeeaf4] text-[8px]"><div class="flex justify-between gap-3 py-3"><dt class="text-[#7b86a1]">Módulo</dt><dd class="truncate text-right font-semibold">{{ model.copy.plural }}</dd></div><div class="flex justify-between gap-3 py-3"><dt class="text-[#7b86a1]">Campos disponíveis</dt><dd class="font-semibold">{{ model.fields.length }}</dd></div><div class="flex justify-between gap-3 py-3"><dt class="text-[#7b86a1]">Preenchidos</dt><dd class="font-semibold text-[#5a24c7]">{{ filledFields }}</dd></div><div v-if="isEdit && hasDetail" class="flex justify-between gap-3 py-3"><dt class="text-[#7b86a1]">Status atual</dt><dd class="truncate text-right font-semibold">{{ status }}</dd></div></dl></article>
          <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4"><h2 class="text-[10px] font-bold">Jornada da operação</h2><div class="mt-4 space-y-3"><div v-for="(stage, index) in journey" :key="stage.id" class="flex gap-3"><span class="grid size-6 place-items-center rounded-full text-[8px] font-bold" :class="index === 0 ? 'bg-[#e9e0ff] text-[#5a24c7]' : 'bg-[#f1eff4] text-[#9ba4bc]'">{{ index + 1 }}</span><span><b class="block text-[8px]">{{ stage.label }}</b><small class="block text-[7px] text-[#7b86a1]">{{ index === 0 ? 'Em preenchimento' : 'Aguardando' }}</small></span></div></div></article>
          <article class="rounded-2xl border border-[#eadff9] bg-gradient-to-br from-white to-[#fff2fc] p-4"><h2 class="text-[10px] font-bold">Validação segura</h2><p class="mt-3 text-[8px] leading-5 text-[#69759c]">A interface preserva o formulário e mostra exatamente a resposta do backend.</p></article>
        </aside>
      </section>
    </template>
    </template>
  </main>
</template>
